import { useEffect, useMemo, useState } from "react";
import { PageHero } from "@/pages/About";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/pages/ImpactHub";

export default function Gallery() {
    const [categories, setCategories] = useState([]);
    const [active, setActive] = useState("");
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        api.get("/public/gallery").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    }, []);

    const shown = useMemo(() => {
        if (!active) return categories.flatMap((c) => c.items.map((it) => ({ ...it, __cat: c.category })));
        const bucket = categories.find((c) => c.category === active);
        return (bucket?.items || []).map((it) => ({ ...it, __cat: bucket.category }));
    }, [categories, active]);

    return (
        <div data-testid="public-gallery">
            <PageHero title="Photo Gallery" subtitle="Authentic photographs from Global Seed Foundation community programmes" />
            <section className="gsf-section">
                <div className="gsf-container">
                    <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-category-filters">
                        <button
                            onClick={() => setActive("")}
                            data-testid="gallery-filter-all"
                            className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${!active ? "bg-[color:var(--gsf-green-700)] text-white" : "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-green-700)]/10"}`}
                        >
                            All categories
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c.category}
                                onClick={() => setActive(c.category)}
                                data-testid={`gallery-filter-${c.category.replace(/\s+/g, "-").toLowerCase()}`}
                                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${active === c.category ? "bg-[color:var(--gsf-green-700)] text-white" : "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-green-700)]/10"}`}
                            >
                                {c.category} <span className="opacity-60">({c.items.length})</span>
                            </button>
                        ))}
                    </div>

                    {shown.length === 0 ? (
                        <div className="gsf-card p-8 text-sm text-gray-500 max-w-xl" data-testid="gallery-empty">
                            No photographs published for this category yet.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
                            {shown.map((it, i) => (
                                <figure key={`${it.url}-${i}`} className="group cursor-zoom-in" data-testid={`gallery-item-${i}`}>
                                    <button
                                        type="button"
                                        onClick={() => setLightbox(it)}
                                        className="block w-full aspect-square rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-[color:var(--gsf-gold-500)]/40"
                                    >
                                        <img
                                            src={resolveMediaUrl(it.url)}
                                            alt={it.alt_text || it.caption || it.__cat}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </button>
                                    <figcaption className="mt-2 text-xs text-[color:var(--gsf-body)] leading-snug">
                                        <span className="uppercase tracking-widest text-[10px] font-semibold text-[color:var(--gsf-green-600)]">{it.__cat}</span>
                                        {it.caption && <div className="mt-1 line-clamp-2">{it.caption}</div>}
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {lightbox && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
                    onClick={() => setLightbox(null)}
                    data-testid="gallery-lightbox"
                >
                    <figure className="max-w-6xl w-full">
                        <img src={resolveMediaUrl(lightbox.url)} alt={lightbox.alt_text || ""} className="w-full max-h-[80vh] object-contain" />
                        {lightbox.caption && (
                            <figcaption className="mt-4 text-white/90 text-sm text-center leading-relaxed" data-testid="lightbox-caption">
                                {lightbox.caption}
                                {lightbox.programme && <span className="block text-white/50 text-xs mt-1">{lightbox.programme}</span>}
                            </figcaption>
                        )}
                    </figure>
                </div>
            )}
        </div>
    );
}
